import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  createAuthBaseQuery,
  getCsrfTokenFromCookies,
} from "../../common/utils";
import { HOSTNAME } from "../../constant/host";

// ===== 型定義 =====

export interface SendInvitationRequest {
  projectId: string;
  email: string;
  ip_restrictions: string[];
  is_manager: boolean;
}

export interface InvitationRecord {
  user_id: number;
  email: string;
  display_name?: string;
  avatar_url?: string | null;
  position: string;
  is_new_user: boolean;
}

interface InvitationsListResponse {
  status: string;
  count: number;
  data: InvitationRecord[];
}

interface InvitationActionResponse {
  status: string;
  message?: string;
}

export interface VerifyTokenResponse {
  status: "existing_user_logged_in" | "existing_user_not_logged_in" | "new_user" | "already_accepted";
  project_id: string;
  project_name?: string;
  email?: string;
  /** オファー招待（クリエイター招待）のとき "offer"。クライアント招待では undefined / "project"。 */
  invite_type?: "project" | "offer";
  /** オファー招待のとき、遷移先となるオファースレッド（OfferProject.offer_id）。 */
  offer_id?: string;
  /** オファー招待のとき、オファー送信者の表示名（サインイン画面の見出しに表示）。 */
  sender_name?: string;
  /** オファー招待のとき、オファー概要（「ロール（スコープ）」形式）。書類確認の
   *  フォールバック表示に使う。 */
  offer_summary?: string;
  /** オファー招待のとき、依頼ロール（assigned_roles）。offer_summary のフォールバック。 */
  offer_role?: string;
  /** オファー招待のとき、報酬（税込合計）。 */
  offer_reward?: number | null;
  /** オファー招待のとき、納期（ISO 8601）。 */
  offer_deadline?: string;
}

export interface SelfResendInvitationResponse {
  status: string;
  message?: string;
  /** 再送先のメールアドレス（招待トークンに埋め込まれた本人の宛先）。 */
  email?: string;
}

export interface AcceptInvitationResponse {
  status: string;
  project_id: string;
  /** オファー招待経由のとき、遷移先となるオファースレッド ID。 */
  offer_id?: string;
}

export interface RegisterFromInvitationResponse {
  status: string;
  message?: string;
  token: string;
  project_id: string;
  /**
   * 新規登録されたユーザーの ID（`/api/me` の `data.id` と同一）。
   * UserItem の `expectedUserId` ガードで別ユーザーの /api/me 結果を弾くために使用する。
   */
  user_id: number;
  /** オファー招待経由の登録のとき、遷移先となるオファースレッド ID。 */
  offer_id?: string;
}

// ===== 認証必要なAPIサービス =====

export const invitationsApi = createApi({
  reducerPath: "invitationsApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["Invitations"],
  endpoints: (builder) => ({
    // 招待送信
    sendInvitation: builder.mutation<InvitationActionResponse, SendInvitationRequest>({
      query: ({ projectId, ...body }) => ({
        url: `/api/${projectId}/invitations/`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: "Invitations", id: projectId },
      ],
    }),

    // 招待一覧取得
    getInvitations: builder.query<InvitationsListResponse, string>({
      query: (projectId) => ({
        url: `/api/${projectId}/invitations/`,
        method: "GET",
      }),
      providesTags: (_result, _error, projectId) => [
        { type: "Invitations", id: projectId },
      ],
    }),

    // 招待再送信
    resendInvitation: builder.mutation<InvitationActionResponse, { projectId: string; userId: number }>({
      query: ({ projectId, userId }) => ({
        url: `/api/${projectId}/invitations/${userId}/resend/`,
        method: "PUT",
      }),
    }),

    // 招待キャンセル
    cancelInvitation: builder.mutation<InvitationActionResponse, { projectId: string; userId: number }>({
      query: ({ projectId, userId }) => ({
        url: `/api/${projectId}/invitations/${userId}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: "Invitations", id: projectId },
      ],
    }),

    // 招待承認（既存ユーザー用）
    acceptInvitation: builder.mutation<AcceptInvitationResponse, { token: string }>({
      query: (body) => ({
        url: `/api/invitations/accept/`,
        method: "POST",
        body,
      }),
    }),
  }),
});

// ===== 未認証でもアクセス可能なAPIサービス =====

export const publicInvitationsApi = createApi({
  reducerPath: "publicInvitationsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: HOSTNAME,
    credentials: "include",
    // 招待の検証・新規登録は「旧ユーザーの認証を持ち込まない」ことが重要なため、
    // auth_token を Authorization ヘッダに付与しない。CSRF トークンのみ送出する。
    prepareHeaders: (headers) => {
      const csrfToken = getCsrfTokenFromCookies();
      if (csrfToken) {
        headers.set("X-CSRFToken", csrfToken);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    // 招待トークン検証
    verifyInvitationToken: builder.mutation<VerifyTokenResponse, { token: string }>({
      query: (body) => ({
        url: `/api/invitations/verify/`,
        method: "POST",
        body,
      }),
    }),

    // 期限切れ招待リンクからの再送（受信者本人が期限切れ画面から実行する）
    resendSelfInvitation: builder.mutation<SelfResendInvitationResponse, { token: string }>({
      query: (body) => ({
        url: `/api/invitations/resend/`,
        method: "POST",
        body,
      }),
    }),

    // 新規ユーザー登録（招待経由）
    registerFromInvitation: builder.mutation<RegisterFromInvitationResponse, FormData>({
      query: (formData) => ({
        url: `/api/invitations/register/`,
        method: "POST",
        body: formData,
      }),
    }),
  }),
});

export const {
  useSendInvitationMutation,
  useGetInvitationsQuery,
  useResendInvitationMutation,
  useCancelInvitationMutation,
  useAcceptInvitationMutation,
} = invitationsApi;

export const {
  useVerifyInvitationTokenMutation,
  useResendSelfInvitationMutation,
  useRegisterFromInvitationMutation,
} = publicInvitationsApi;
